import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Loader2, X, CheckSquare } from "lucide-react";
import AdminPageHeader from "@/components/AdminPageHeader";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function ProductsTab({ products, fetchAllData }) {
  const [open, setOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, product: null });
  // Bulk-select state. When `bulkDeleteOpen` is true, the confirmation dialog
  // is shown for deleting all selected products.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    minimum_quantity: "1",
    color: "#ffffff",
    category_id: "",
    overdue_days: "",
    is_default_extra: false,
  });
  const [loading, setLoading] = useState(false);
  
  // Category management states
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: "", description: "" });
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState({ open: false, category: null });

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { headers: getAuthHeader() });
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        name: formData.name,
        minimum_quantity: parseInt(formData.minimum_quantity),
        color: formData.color,
        category_id: formData.category_id && formData.category_id !== "none" ? formData.category_id : null,
        overdue_days: formData.overdue_days ? parseInt(formData.overdue_days) : null,
        is_default_extra: !!formData.is_default_extra,
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data, {
          headers: getAuthHeader(),
        });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, data, { headers: getAuthHeader() });
        toast.success("Product created");
      }

      setOpen(false);
      setEditingProduct(null);
      setFormData({ name: "", minimum_quantity: "1", color: "#ffffff", category_id: "", overdue_days: "", is_default_extra: false });
      fetchAllData();
    } catch (error) {
      toast.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryFormData, {
          headers: getAuthHeader(),
        });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/categories`, categoryFormData, { headers: getAuthHeader() });
        toast.success("Category created");
      }

      setShowCategoryDialog(false);
      setEditingCategory(null);
      setCategoryFormData({ name: "", description: "" });
      fetchCategories();
    } catch (error) {
      toast.error("Category operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategoryDialog.category) return;

    setLoading(true);
    try {
      await axios.delete(`${API}/categories/${deleteCategoryDialog.category.id}`, { headers: getAuthHeader() });
      toast.success("Category deleted");
      setDeleteCategoryDialog({ open: false, category: null });
      fetchCategories();
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete category");
    } finally {
      setLoading(false);
    }
  };

  // Filter products by category
  const filteredProducts = categoryFilter === "all" 
    ? products 
    : categoryFilter === "uncategorized"
    ? products.filter(p => !p.category_id)
    : products.filter(p => p.category_id === categoryFilter);

  const confirmDelete = async () => {
    if (!deleteDialog.product) return;

    setLoading(true);
    try {
      await axios.delete(`${API}/products/${deleteDialog.product.id}`, { headers: getAuthHeader() });
      toast.success("Product deleted");
      setDeleteDialog({ open: false, product: null });
      fetchAllData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete product";
      toast.error(errorMsg, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  // ---------- Bulk-select helpers ----------
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const toggleSelectAllFiltered = (filteredList) => {
    setSelectedIds((prev) => {
      const allSelected = filteredList.every((p) => prev.has(p.id));
      const next = new Set(prev);
      if (allSelected) {
        filteredList.forEach((p) => next.delete(p.id));
      } else {
        filteredList.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    // One bulk request — much faster than N parallel calls because the backend
    // resolves the whole batch in a single DB transaction.
    let okCount = 0;
    let failedMap = {};
    try {
      const res = await axios.post(
        `${API}/products/bulk-delete`,
        { ids, force: true },
        { headers: getAuthHeader() }
      );
      okCount = res.data?.deleted_count || 0;
      failedMap = res.data?.failed || {};
      if (okCount > 0) toast.success(`Deleted ${okCount} product${okCount === 1 ? "" : "s"}`);
      const failedIds = Object.keys(failedMap);
      if (failedIds.length > 0) {
        const firstReason = failedMap[failedIds[0]] || "Unknown reason";
        toast.error(`${failedIds.length} failed: ${firstReason}`, { duration: 6000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    }
    setBulkDeleting(false);
    setBulkDeleteOpen(false);
    setSelectedIds(new Set(Object.keys(failedMap)));
    fetchAllData();
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      minimum_quantity: product.minimum_quantity.toString(),
      color: product.color || "#ffffff",
      category_id: product.category_id || "",
      overdue_days: product.overdue_days ? product.overdue_days.toString() : "",
      is_default_extra: !!product.is_default_extra,
    });
    setOpen(true);
  };

  return (
    <Card className="shadow-lg border-0">
      <CardHeader>
        <AdminPageHeader
          title="Products"
          description="Manage your product catalog (prices set per customer)"
          actions={
            <>
              {selectedIds.size > 0 && (
                <>
                  <Button
                    variant="outline"
                    className="rounded-sm text-red-700 border-red-300 hover:bg-red-50"
                    onClick={() => setBulkDeleteOpen(true)}
                    data-testid="bulk-delete-products-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete ({selectedIds.size})
                  </Button>
                  <Button
                    variant="ghost"
                    className="rounded-sm"
                    onClick={clearSelection}
                    data-testid="clear-product-selection"
                  >
                    <X className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Clear</span>
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => {
                  setShowCategoryDialog(true);
                  setEditingCategory(null);
                  setCategoryFormData({ name: "", description: "" });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Manage Categories</span>
                <span className="sm:hidden">Categories</span>
              </Button>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-product-button"
                    onClick={() => {
                      setEditingProduct(null);
                      setFormData({ name: "", minimum_quantity: "1", color: "#ffffff", category_id: "", overdue_days: "", is_default_extra: false });
                    }}
                    className="bg-brand-navy hover:bg-brand-navy-hover text-white rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
                <DialogDescription>
                  {editingProduct ? "Update product details" : "Create a new product"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    data-testid="product-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="min-qty">Minimum Quantity</Label>
                  <Input
                    id="min-qty"
                    data-testid="product-min-qty-input"
                    type="number"
                    min="1"
                    value={formData.minimum_quantity}
                    onChange={(e) => setFormData({ ...formData, minimum_quantity: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="overdue-days">Overdue threshold (days)</Label>
                  <Input
                    id="overdue-days"
                    data-testid="product-overdue-days"
                    type="number"
                    min="0"
                    value={formData.overdue_days}
                    onChange={(e) => setFormData({ ...formData, overdue_days: e.target.value })}
                    placeholder="e.g. 30 — leave blank to disable"
                  />
                  <p className="text-sm text-gray-500">
                    Items at a hotel longer than this many days will be flagged as overdue.
                    Leave blank for products that don&apos;t need tracking (e.g. linen carts).
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="color">Dashboard Color</Label>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-lg border-2 shadow-sm"
                      style={{ backgroundColor: formData.color }}
                    />
                    <Input
                      id="color"
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-24 h-12 cursor-pointer"
                    />
                    <span className="text-sm text-gray-500">Used in charts and graphs</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category (Optional)</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Category</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-start gap-3 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2.5 cursor-pointer hover:bg-neutral-100 transition-colors">
                  <input
                    type="checkbox"
                    data-testid="product-is-default-extra"
                    checked={!!formData.is_default_extra}
                    onChange={(e) => setFormData({ ...formData, is_default_extra: e.target.checked })}
                    className="mt-1 h-4 w-4"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold">Default add to every new bag</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      When picking, this product is auto-included as an extra item (qty 1) in
                      every new bag. The operator can adjust the quantity or remove it inside
                      the dialog. Typical use: laundry bags, reject bags.
                    </span>
                  </span>
                </label>
                <Button
                  type="submit"
                  data-testid="submit-product-button"
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : editingProduct ? (
                    "Update Product"
                  ) : (
                    "Create Product"
                  )}
                </Button>
              </form>
            </DialogContent>
              </Dialog>
            </>
          }
        />
      </CardHeader>
      <CardContent>
        {/* Category Filter */}
        <div className="mb-4 flex gap-2 flex-wrap">
          <Button
            variant={categoryFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setCategoryFilter("all")}
          >
            All ({products.length})
          </Button>
          {categories.map(cat => {
            const count = products.filter(p => p.category_id === cat.id).length;
            return (
              <Button
                key={cat.id}
                variant={categoryFilter === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setCategoryFilter(cat.id)}
              >
                {cat.name} ({count})
              </Button>
            );
          })}
          <Button
            variant={categoryFilter === "uncategorized" ? "default" : "outline"}
            size="sm"
            onClick={() => setCategoryFilter("uncategorized")}
          >
            Uncategorized ({products.filter(p => !p.category_id).length})
          </Button>
        </div>

        {/* Select-all toggle for the currently-filtered list */}
        {filteredProducts.length > 0 && (
          <div className="flex items-center gap-2 mb-3 pl-1">
            <Checkbox
              id="select-all-products"
              data-testid="select-all-products"
              checked={
                filteredProducts.length > 0 &&
                filteredProducts.every((p) => selectedIds.has(p.id))
              }
              onCheckedChange={() => toggleSelectAllFiltered(filteredProducts)}
            />
            <label
              htmlFor="select-all-products"
              className="text-sm text-muted-foreground cursor-pointer select-none"
            >
              <CheckSquare className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : "Select all in view"}
            </label>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => {
            const isSelected = selectedIds.has(product.id);
            return (
            <Card
              key={product.id}
              data-testid={`product-card-${product.id}`}
              className={`border-2 transition-colors ${isSelected ? "border-brand-navy ring-2 ring-brand-navy/30" : ""}`}
            >
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4 gap-2">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelected(product.id)}
                      data-testid={`select-product-${product.id}`}
                      className="mt-1.5"
                    />
                    <div
                      className="w-8 h-8 rounded-lg border-2 shadow-sm flex-shrink-0"
                      style={{ backgroundColor: product.color || "#ffffff" }}
                      title={`Dashboard color: ${product.color || "#ffffff"}`}
                    />
                    <div className="min-w-0">
                      <h3 className="font-semibold text-lg break-words">{product.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Min. qty: {product.minimum_quantity}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => openEditDialog(product)}
                      data-testid={`edit-product-${product.id}`}
                      variant="outline"
                      size="icon"
                      aria-label="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setDeleteDialog({ open: true, product })}
                      data-testid={`delete-product-${product.id}`}
                      variant="destructive"
                      size="icon"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, product: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product?</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-2">Are you sure you want to delete this product?</p>
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <p className="font-semibold">{deleteDialog.product?.name}</p>
                <p className="text-sm text-gray-600">Min. Quantity: {deleteDialog.product?.minimum_quantity}</p>
              </div>
              <p className="mt-2 text-red-600 font-semibold">This action cannot be undone!</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialog({ open: false, product: null })}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? "Deleting..." : "Yes, Delete Product"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} products?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected products and automatically
              remove them from every user / hotel allocation. Products referenced
              by <strong>pending</strong> orders cannot be deleted and will be
              reported as failures. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={bulkDeleting}
              data-testid="bulk-delete-confirm"
            >
              {bulkDeleting ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Deleting…</span>
              ) : (
                `Yes, delete ${selectedIds.size}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Category Management Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
            <DialogDescription>Create and manage product categories</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                value={categoryFormData.name}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                placeholder="e.g., Linens, Towels"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={categoryFormData.description}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? "Saving..." : editingCategory ? "Update Category" : "Create Category"}
            </Button>
          </form>

          <div className="border-t pt-4 mt-4">
            <h4 className="font-semibold mb-3">Existing Categories ({categories.length})</h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {categories.map(cat => (
                <div key={cat.id} className="flex items-center justify-between p-2 border rounded hover:bg-gray-50">
                  <div className="flex-1">
                    <p className="font-medium">{cat.name}</p>
                    {cat.description && <p className="text-sm text-gray-500">{cat.description}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingCategory(cat);
                        setCategoryFormData({ name: cat.name, description: cat.description || "" });
                      }}
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeleteCategoryDialog({ open: true, category: cat })}
                      className="text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
              {categories.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">No categories yet. Create one above!</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <AlertDialog open={deleteCategoryDialog.open} onOpenChange={(open) => setDeleteCategoryDialog({ open, category: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-2">Are you sure you want to delete this category?</p>
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <p className="font-semibold">{deleteCategoryDialog.category?.name}</p>
                {deleteCategoryDialog.category?.description && (
                  <p className="text-sm text-gray-600">{deleteCategoryDialog.category.description}</p>
                )}
              </div>
              <p className="mt-2 text-orange-600 font-semibold">Products will be unassigned from this category.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteCategoryDialog({ open: false, category: null })}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? "Deleting..." : "Yes, Delete Category"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}